import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Badge,
  Button,
  Checkbox,
  Menu,
  MenuItem,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

const ToolDasthRouteNotifications = ({ routes, nuid, ridersNames }) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [page, setPage] = useState(0);
  const [readNotifications, setReadNotifications] = useState(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(`readNotifications_${nuid}`)
      );
      return new Set(
        Array.isArray(stored?.notifications) ? stored.notifications : []
      );
    } catch {
      return new Set();
    }
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [filterOption, setFilterOption] = useState([]);
  const observerRefs = useRef(new Map());
  const observer = useRef(null);
  const spokenIdsRef = useRef(new Set());

  const pageSize = 30;
  const today = new Date().toISOString().slice(0, 10);

  // Cargar y resetear lectura diaria
  useEffect(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(`readNotifications_${nuid}`)
      );
      if (!stored || stored.date !== today) {
        localStorage.setItem(
          `readNotifications_${nuid}`,
          JSON.stringify({ date: today, notifications: [] })
        );
        setReadNotifications(new Set());
      } else {
        setReadNotifications(new Set(stored.notifications));
      }
    } catch {
      setReadNotifications(new Set());
    }
  }, [nuid]);

  // Guardar cambios
  useEffect(() => {
    localStorage.setItem(
      `readNotifications_${nuid}`,
      JSON.stringify({
        date: today,
        notifications: Array.from(readNotifications),
      })
    );
  }, [readNotifications, nuid]);

  const uniqueRoutes = Array.from(
    new Map(routes.map((r) => [r.id, r])).values()
  );

  const raw = uniqueRoutes.flatMap((route) => route.observations || []);

  // 2) Creamos un Map para quedarnos sólo con la primera aparición de cada obs.id
  const uniqMap = new Map();
  raw.forEach((obs) => {
    const key = obs.id;
    if (!uniqMap.has(key)) {
      uniqMap.set(key, {
        id: obs.id,
        orderID: obs.orderID,
        message: obs.comment,
        createdAt: obs.createdAt,
        createdBy: obs.createdBy || "System",
        driverID: ridersNames[obs.driverID] || null,
      });
    }
  });

  // 3) Generamos el array único y lo ordenamos
  const allObservations = Array.from(uniqMap.values()).sort(
    (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
  );

  const unreadObservations = allObservations.filter(
    (obs) => !readNotifications.has(obs.id)
  ).length;

  const toggleDrawer = (open) => () => setOpenDrawer(open);

  const markAsRead = useCallback((id) => {
    setReadNotifications((prev) => new Set(prev).add(id));
  }, []);

  // Paginación
  const paginatedObservations = allObservations.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  // IntersectionObserver para marcar lecturas al hacer scroll
  useEffect(() => {
    if (!observer.current) {
      observer.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const id = entry.target.getAttribute("data-id");
            if (entry.isIntersecting && id && !readNotifications.has(id)) {
              markAsRead(id);
            }
          });
        },
        { threshold: 0.7 }
      );
    }
    paginatedObservations.forEach((obs) => {
      const el = observerRefs.current.get(obs.id);
      if (el) observer.current.observe(el);
    });
    return () => observer.current.disconnect();
  }, [paginatedObservations, markAsRead]);

  // Helper de filtro
  const matchesFilter = (message) => {
    const text = message?.toLowerCase() || "";
    if (filterOption.length === 0) return true;
    return filterOption.some((opt) => {
      if (opt === "entregado") return text.includes("entregado");
      if (opt === "visitado") return text.includes("visitado");
      if (opt === "ruta") return text.includes("ruta generada automáticamente");
      return false;
    });
  };

  const filteredObservations = paginatedObservations.filter((obs) =>
    matchesFilter(obs.message)
  );

  // Lectura automática de nuevas notificaciones filtradas
  useEffect(() => {
    if (!voiceEnabled) return;
    const candidates = filteredObservations.filter(
      (obs) =>
        !readNotifications.has(obs.id) && !spokenIdsRef.current.has(obs.id)
    );
    if (candidates.length === 0) return;
    speechSynthesis.cancel();
    spokenIdsRef.current.clear();
    candidates.forEach((obs, i) => {
      setTimeout(() => {
        let text = "";

        if (obs.message?.toLowerCase().includes("entregado")) {
          text = "Entrega en línea";
        } else if (obs.message?.toLowerCase().includes("visitado")) {
          text = "Visitado";
        } else if (obs.message?.toLowerCase().includes("asignacion")) {
          text = "A Ruta";
        }

        if (text) {
          const utter = new SpeechSynthesisUtterance(text);
          speechSynthesis.speak(utter);
          spokenIdsRef.current.add(obs.id);
        }
      }, i * 1000);
    });
  }, [filteredObservations, readNotifications, voiceEnabled]);

  // Al togglear voz o filtro, reiniciar lectura
  useEffect(() => {
    speechSynthesis.cancel();
    spokenIdsRef.current.clear();
  }, [voiceEnabled, filterOption]);

  const handleFilterClick = (e) => setAnchorEl(e.currentTarget);
  const handleFilterClose = (opt) => {
    setFilterOption((prev) => {
      if (!opt) return prev;
      return prev.includes(opt)
        ? prev.filter((v) => v !== opt)
        : [...prev, opt];
    });
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = () => {
    setReadNotifications(new Set(allObservations.map((o) => o.id)));
    speechSynthesis.cancel();
    spokenIdsRef.current.clear();
  };

  return (
    <Box sx={{ position: "relative" }}>
      <IconButton onClick={toggleDrawer(true)} color="primary">
        <Badge badgeContent={unreadObservations} color="error">
          <NotificationsIcon fontSize="large" />
        </Badge>
      </IconButton>

      <Drawer anchor="right" open={openDrawer} onClose={toggleDrawer(false)}>
        <Box
          sx={{
            width: 400,
            height: "100vh",
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" fontWeight="bold" mb={2}>
            🔔 Notificaciones ({unreadObservations} nuevas)
          </Typography>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <IconButton onClick={handleFilterClick}>
                <FilterListIcon />
              </IconButton>
              <Button
                variant={voiceEnabled ? "contained" : "outlined"}
                color="secondary"
                onClick={() => setVoiceEnabled((v) => !v)}
              >
                🔊 {voiceEnabled ? "Voz activada" : "Leer comentarios"}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => handleFilterClose()}
              >
                <MenuItem onClick={() => handleFilterClose("entregado")}>
                  <Checkbox checked={filterOption.includes("entregado")} /> Solo
                  entregados
                </MenuItem>
                <MenuItem onClick={() => handleFilterClose("visitado")}>
                  <Checkbox checked={filterOption.includes("visitado")} /> Solo
                  visitados
                </MenuItem>
                <MenuItem onClick={() => handleFilterClose("ruta")}>
                  <Checkbox checked={filterOption.includes("ruta")} /> Solo
                  rutas generadas
                </MenuItem>
              </Menu>
            </Box>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
            >
              Marcar todas como leídas
            </Button>
          </Box>

          <List sx={{ flexGrow: 1, overflowY: "auto" }}>
            {filteredObservations.length > 0 ? (
              filteredObservations.map((obs, idx) => {
                const isRead = readNotifications.has(obs.id);
                return (
                  <React.Fragment key={idx}>
                    <ListItem
                      data-id={obs.id}
                      ref={(el) => observerRefs.current.set(obs.id, el)}
                      alignItems="flex-start"
                      sx={{
                        mb: 1,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: isRead ? "#F3F3F3" : "#c9a0ef",
                        cursor: "pointer",
                      }}
                      onClick={() => markAsRead(obs.id)}
                    >
                      <ListItemAvatar>
                        <Avatar>
                          <AccountCircleIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center">
                            <ChatBubbleOutlineIcon sx={{ mr: 0.5 }} />
                            <Typography fontWeight="bold">
                              {obs.createdBy} {obs.orderID}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography>{obs.message}</Typography>
                            {obs.driverID && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                🚚 Driver: {obs.driverID}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              🕒{" "}
                              {obs.createdAt?.seconds
                                ? new Date(
                                    obs.createdAt.seconds * 1000
                                  ).toLocaleString()
                                : "Sin fecha"}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })
            ) : (
              <Typography align="center" color="text.secondary">
                No hay notificaciones recientes
              </Typography>
            )}
          </List>

          {allObservations.length > pageSize && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
            >
              <Button
                onClick={() => setPage((p) => Math.max(p - 1, 0))}
                disabled={page === 0}
              >
                Anterior
              </Button>
              <Typography>
                Página {page + 1} de{" "}
                {Math.ceil(allObservations.length / pageSize)}
              </Typography>
              <Button
                onClick={() =>
                  setPage((p) =>
                    p + 1 < Math.ceil(allObservations.length / pageSize)
                      ? p + 1
                      : p
                  )
                }
                disabled={
                  page + 1 >= Math.ceil(allObservations.length / pageSize)
                }
              >
                Siguiente
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default ToolDasthRouteNotifications;
